import { zLabWorkbookSummaryResponse, type LabWorkbookSummaryResponse } from '@resistapp/common/api-types';
import type { GenePanelConfigurationId } from '@resistapp/common/gene-panels';
import { useQuery } from '@tanstack/react-query';
import type { DispenserPrefix } from '@resistapp/common/types';

import { getAuthHeaders, resistomapApiClient } from '../../../utils/fetch';

export type SmartChipGrid = {
  rowLabels: string[];
  columnLabels: Array<string | number>;
  values: Array<Array<string | null>>;
  caption?: string | null;
};

export type SmartChipSampleLayout = {
  chipIndex: number | null;
  chipLabel: string | null;
  primary: SmartChipGrid;
  secondary?: SmartChipGrid | null;
  sampleCount: number;
  samplesPerChip: number | null;
  isComplete: boolean;
};

export type SmartChipRunLogEntry = {
  id: string | null;
  chipId: number | null;
  chipIndex: number | null;
  chipName: string | null;
  runDateIso: string | null;
  comment: string | null;
  invoice: string | null;
  dispenser: number | null;
  dispenserPrefix: DispenserPrefix | null;
  cycler: number | null;
  kitLot: string | null;
  mmLot: string | null;
  waterLot: string | null;
  glycerolLot: string | null;
  antifoamBLot: string | null;
  preparerInitials: string | null;
  uploaderInitials: string | null;
};

export type SmartChipRunSummary = {
  chips: Array<{
    id: number;
    name: string;
    runDateIso: string | null;
    mmLot: string | null;
    waterLot: string | null;
    glycerolLot: string | null;
    antifoamBLot: string | null;
    kitLot: string | null;
    comment: string | null;
    invoice: string | null;
    preparerInitials: string | null;
    uploaderInitials: string | null;
    dispenser: number | null;
    dispenserPrefix: DispenserPrefix | null;
    cycler: number | null;
    files: {
      cts: { filename: string; url: string } | null;
      raw: { filename: string; url: string } | null;
    };
  }>;
  assayGrid: SmartChipGrid | null;
  sampleLayouts: SmartChipSampleLayout[];
  suggestedChipCount: number | null;
  runLogs: SmartChipRunLogEntry[];
  chipsToRun?: number | null;
  selectedLayoutIndex?: number | null;
  configurationId?: GenePanelConfigurationId | null;
};

export type LabWorkbookSummary = LabWorkbookSummaryResponse & {
  smartChip?: SmartChipRunSummary | null;
};

async function fetchLabWorkbookSummary(workbookId: string): Promise<LabWorkbookSummary> {
  const response = await resistomapApiClient.get<LabWorkbookSummaryResponse>(`/lab/${workbookId}`, {
    headers: getAuthHeaders('application/json'),
  });
  const parsed = zLabWorkbookSummaryResponse.parse(response.data);
  return parsed as LabWorkbookSummary;
}

export function useLabWorkbook(workbookId: string | null) {
  return useQuery({
    queryKey: ['labWorkbook', workbookId],
    queryFn: async () => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      return await fetchLabWorkbookSummary(workbookId);
    },
    enabled: Boolean(workbookId),
    staleTime: 30_000,
  });
}
